import { useEffect, useState, useRef, useCallback } from 'react'
import Container from 'react-bootstrap/Container'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { Capacitor } from '@capacitor/core'
import kioskTips from '../images/kiosk-tips.png'
import kioskCheesy2 from '../images/kiosk-cheesy2.png'
import tap from '../images/tap.png'
import WalletCarousel from '../components/WalletCarousel'
import dijittoLogo from '../images/dijitto-express-logo.svg'
import dogeIcon from '../images/dogeIcon.png'
import btcIcon from '../images/btcIcon.png'
import ltcIcon from '../images/ltcIcon.png'

export const Splash = props => {
  // Check if running on web (not mobile app)
  const isWeb = Capacitor.getPlatform() === 'web';

  // Phase 1: State Management for Dynamic Crypto Logos
  const [configuredCryptos, setConfiguredCryptos] = useState(['BTC', 'LTC', 'DOGE'])
  const [isLoading, setIsLoading] = useState(true)

  // Phase 1: Wallet Detection Function
  const detectConfiguredWallets = async () => {
    try {
      // Check which wallets are configured by looking for xpub keys in AsyncStorage
      // These are the actual keys used by Admin.js to store wallet data
      const dogeXpub = await AsyncStorage.getItem('api-xpub-doge')
      const btcXpub = await AsyncStorage.getItem('api-xpub-btc')
      const ltcXpub = await AsyncStorage.getItem('api-xpub-ltc')

      console.log('Raw AsyncStorage xpub values:')
      console.log('  api-xpub-doge:', dogeXpub ? 'exists' : 'null')
      console.log('  api-xpub-btc:', btcXpub ? 'exists' : 'null')
      console.log('  api-xpub-ltc:', ltcXpub ? 'exists' : 'null')

      const configured = []

      // Check if each wallet is configured (xpub exists and is not null)
      if (dogeXpub && dogeXpub !== 'null') {
        console.log('  ✅ DOGE wallet configured')
        configured.push('DOGE')
      }

      if (btcXpub && btcXpub !== 'null') {
        console.log('  ✅ BTC wallet configured')
        configured.push('BTC')
      }

      if (ltcXpub && ltcXpub !== 'null') {
        console.log('  ✅ LTC wallet configured')
        configured.push('LTC')
      }

      return configured
    } catch (error) {
      console.error('Error detecting wallets:', error)
      // Fallback: show all 3
      return ['BTC', 'LTC', 'DOGE']
    }
  }

  // Phase 1: Load Wallet Configuration on Mount
  useEffect(() => {
    const loadWalletConfig = async () => {
      console.log('=== SPLASH SCREEN WALLET DETECTION ===')
      console.log('storeId:', props.storeId)

      if (!props.storeId) {
        // First-time user: show all 3
        setConfiguredCryptos(['BTC', 'LTC', 'DOGE'])
        setIsLoading(false)
        console.log('First-time user detected - showing all 3 cryptos')
      } else {
        // Activated store: detect configured wallets
        console.log('Activated store - detecting configured wallets...')
        const configured = await detectConfiguredWallets()

        console.log('Detection result:', configured)

        if (configured.length === 0) {
          // Safety fallback: show all 3
          console.log('No wallets configured - falling back to all 3 cryptos')
          setConfiguredCryptos(['BTC', 'LTC', 'DOGE'])
        } else {
          console.log('✅ Configured cryptos detected:', configured)
          console.log('Setting state to show only:', configured)
          setConfiguredCryptos(configured)
        }

        setIsLoading(false)
      }

      console.log('=== END WALLET DETECTION ===')
    }

    loadWalletConfig()
  }, [props.storeId])

  // Debug custom splash text props
  useEffect(() => {
    console.log('=== CUSTOM SPLASH TEXT DEBUG ===');
    console.log('splashImage:', props.splashImage);
    console.log('topText:', props.topText);
    console.log('middleText:', props.middleText);
    console.log('bottomText:', props.bottomText);
    console.log('Should show custom text:', props.splashImage === 'custom' && (props.topText || props.middleText || props.bottomText));
    console.log('=== END DEBUG ===');
  }, [props.splashImage, props.topText, props.middleText, props.bottomText]);

  // ===== OPTION B: FitText Dynamic Measurement =====
  // Refs for measuring DOM elements
  const textContainerRef = useRef(null);
  const measureRef = useRef(null);
  const [customFontSize, setCustomFontSize] = useState(36);

  // Get the longest text line
  const getLongestText = useCallback(() => {
    const texts = [props.topText, props.middleText, props.bottomText].filter(Boolean);
    if (texts.length === 0) return '';
    return texts.reduce((a, b) => a.length > b.length ? a : b);
  }, [props.topText, props.middleText, props.bottomText]);

  // Count how many text lines we have
  const getTextLineCount = useCallback(() => {
    return [props.topText, props.middleText, props.bottomText].filter(Boolean).length;
  }, [props.topText, props.middleText, props.bottomText]);

  // Calculate optimal font size using binary search
  const calculateOptimalFontSize = useCallback(() => {
    const longestText = getLongestText();
    const lineCount = getTextLineCount();

    if (!longestText || lineCount === 0) return 36;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isPortrait = screenHeight > screenWidth;
    const isTablet = screenWidth >= 768;
    const isSmallPhone = screenHeight < 700 && !isTablet;
    const isVerySmallPhone = screenHeight < 600 && !isTablet;

    // Define the available space - be more conservative for small phones
    let availableWidth, availableHeight;

    if (isPortrait) {
      // Width: use less on small phones to ensure no wrapping
      if (isVerySmallPhone) {
        availableWidth = screenWidth * 0.70;
        availableHeight = screenHeight * 0.18; // Very tight for small phones - stay above wallets
      } else if (isSmallPhone) {
        availableWidth = screenWidth * 0.72;
        availableHeight = screenHeight * 0.20; // Tighter for small phones - stay above wallets
      } else if (isTablet) {
        availableWidth = screenWidth * 0.85;
        availableHeight = screenHeight * 0.35;
      } else {
        // Normal phones
        availableWidth = screenWidth * 0.80;
        availableHeight = screenHeight * 0.28;
      }
    } else {
      // Landscape mode - text is in right column
      if (isSmallPhone || isVerySmallPhone) {
        availableWidth = screenWidth * 0.45;
        availableHeight = screenHeight * 0.35;
      } else {
        availableWidth = screenWidth * 0.5;
        availableHeight = screenHeight * 0.4;
      }
    }

    // Calculate height needed per line (font size * line height * number of lines + gaps)
    const lineHeight = 1.3;
    const gapRatio = 0.25; // Gap between lines as ratio of font size

    // Binary search to find optimal font size
    let minSize = 14;
    let maxSize = isTablet ? 120 : (isVerySmallPhone ? 36 : (isSmallPhone ? 42 : 70));
    let optimalSize = minSize;

    // Create a hidden measurement element if needed
    if (!measureRef.current) {
      measureRef.current = document.createElement('div');
      measureRef.current.style.cssText = `
        position: absolute;
        visibility: hidden;
        white-space: nowrap;
        font-family: 'Nunito', Arial, Helvetica, sans-serif;
        font-weight: 700;
      `;
      document.body.appendChild(measureRef.current);
    }

    while (minSize <= maxSize) {
      const midSize = Math.floor((minSize + maxSize) / 2);

      // Measure text width at this font size
      measureRef.current.style.fontSize = `${midSize}px`;
      measureRef.current.textContent = longestText;
      const textWidth = measureRef.current.offsetWidth;

      // Calculate total height needed
      const totalHeight = (midSize * lineHeight * lineCount) + (midSize * gapRatio * (lineCount - 1));

      // Check if it fits - both width AND height must fit
      const fitsWidth = textWidth <= availableWidth;
      const fitsHeight = totalHeight <= availableHeight;

      if (fitsWidth && fitsHeight) {
        optimalSize = midSize;
        minSize = midSize + 1; // Try larger
      } else {
        maxSize = midSize - 1; // Try smaller
      }
    }

    // Apply a safety margin - more aggressive for small phones
    const safetyMargin = isSmallPhone ? 0.90 : 0.95;
    return Math.max(14, Math.floor(optimalSize * safetyMargin));
  }, [getLongestText, getTextLineCount]);

  // Recalculate font size on mount, resize, orientation change, or text change
  useEffect(() => {
    const updateFontSize = () => {
      const newSize = calculateOptimalFontSize();
      setCustomFontSize(newSize);
      console.log(`FitText: Calculated font size = ${newSize}px for "${getLongestText()}" (${getTextLineCount()} lines)`);
    };

    // Delay initial calculation to ensure DOM is ready
    const timeoutId = setTimeout(updateFontSize, 100);

    window.addEventListener('resize', updateFontSize);
    window.addEventListener('orientationchange', updateFontSize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateFontSize);
      window.removeEventListener('orientationchange', updateFontSize);
      // Clean up measurement element
      if (measureRef.current && measureRef.current.parentNode) {
        measureRef.current.parentNode.removeChild(measureRef.current);
        measureRef.current = null;
      }
    };
  }, [props.topText, props.middleText, props.bottomText, calculateOptimalFontSize, getLongestText, getTextLineCount]);

  // Phase 2: Helper - Get icon source for crypto
  const getCryptoIcon = (crypto) => {
    switch(crypto) {
      case 'BTC': return btcIcon
      case 'LTC': return ltcIcon
      case 'DOGE': return dogeIcon
      default: return btcIcon
    }
  }

  // Phase 2: Helper - Get full crypto name
  const getCryptoName = (crypto) => {
    switch(crypto) {
      case 'BTC': return 'Bitcoin'
      case 'LTC': return 'Litecoin'
      case 'DOGE': return 'Dogecoin'
      default: return crypto
    }
  }

  // Phase 2: Triangle Layout (3 cryptos - portrait)
  const renderTriangleLayout = (cryptos, size, gap) => {
    // Always DOGE on top, BTC and LTC on bottom (original layout)
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: `${gap}px`,
        marginBottom: '20px'
      }}>
        <div>
          <img
            src={dogeIcon}
            alt="Dogecoin"
            className="crypto-logo doge-logo"
            style={{height: `${size}px`, width: `${size}px`}}
          />
        </div>
        <div style={{display: 'flex', gap: `${gap}px`}}>
          <img
            src={btcIcon}
            alt="Bitcoin"
            className="crypto-logo"
            style={{height: `${size}px`, width: `${size}px`}}
          />
          <img
            src={ltcIcon}
            alt="Litecoin"
            className="crypto-logo"
            style={{height: `${size}px`, width: `${size}px`}}
          />
        </div>
      </div>
    )
  }

  // Phase 2: Side-by-Side Layout (2 cryptos - portrait)
  const renderSideBySideLayout = (cryptos, size, gap) => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${gap}px`,
        marginBottom: '20px'
      }}>
        {cryptos.map(crypto => (
          <img
            key={crypto}
            src={getCryptoIcon(crypto)}
            alt={getCryptoName(crypto)}
            className={crypto === 'DOGE' ? 'crypto-logo doge-logo' : 'crypto-logo'}
            style={{height: `${size}px`, width: `${size}px`}}
          />
        ))}
      </div>
    )
  }

  // Phase 2: Single Logo Layout (1 crypto - portrait, enlarged)
  const renderSingleLogoLayout = (crypto, size) => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20px'
      }}>
        <img
          src={getCryptoIcon(crypto)}
          alt={getCryptoName(crypto)}
          className={crypto === 'DOGE' ? 'crypto-logo doge-logo' : 'crypto-logo'}
          style={{height: `${size}px`, width: `${size}px`}}
        />
      </div>
    )
  }

  // Phase 2: Horizontal Layout (landscape - all counts)
  const renderHorizontalLayout = (cryptos, size, gap) => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${gap}px`,
        marginBottom: '15px'
      }}>
        {cryptos.map(crypto => (
          <img
            key={crypto}
            src={getCryptoIcon(crypto)}
            alt={getCryptoName(crypto)}
            className={crypto === 'DOGE' ? 'crypto-logo doge-logo' : 'crypto-logo'}
            style={{height: `${size}px`, width: `${size}px`}}
          />
        ))}
      </div>
    )
  }

  // Phase 2: Main Portrait Rendering Logic
  const renderPortraitLogos = (cryptos, isIPad) => {
    const count = cryptos.length

    // Detect small phones in portrait mode (height < 700px typically means small phone)
    const isVerySmallPhone = window.innerHeight < 600;
    const isSmallPhone = window.innerHeight < 700;

    if (count === 3) {
      // Triangle layout - scale down for small phones
      let size, gap;
      if (isIPad) {
        size = 210;
        gap = 30;
      } else if (isVerySmallPhone) {
        size = 85;
        gap = 12;
      } else if (isSmallPhone) {
        size = 100;
        gap = 18;
      } else {
        size = 120;
        gap = 30;
      }
      return renderTriangleLayout(cryptos, size, gap)
    } else if (count === 2) {
      // Side-by-side layout
      let size, gap;
      if (isIPad) {
        size = 210;
        gap = 40;
      } else if (isVerySmallPhone) {
        size = 85;
        gap = 12;
      } else if (isSmallPhone) {
        size = 100;
        gap = 18;
      } else {
        size = 120;
        gap = 30;
      }
      return renderSideBySideLayout(cryptos, size, gap)
    } else if (count === 1) {
      // Single logo, 50% larger
      let size;
      if (isIPad) {
        size = 315;
      } else if (isVerySmallPhone) {
        size = 120;
      } else if (isSmallPhone) {
        size = 150;
      } else {
        size = 180;
      }
      return renderSingleLogoLayout(cryptos[0], size)
    }

    return null
  }

  // Phase 2: Main Landscape Rendering Logic
  const renderLandscapeLogos = (cryptos, isIPad) => {
    const count = cryptos.length

    if (isIPad) {
      // iPad landscape: Use horizontal line layout for all counts
      const size = 210
      const gap = count === 2 ? 40 : 25
      return renderHorizontalLayout(cryptos, size, gap)
    } else {
      // iPhone landscape: Scale logos based on viewport height
      // Very small phones (< 350px height) get even smaller logos to prevent cutoff
      const isVerySmallPhone = window.innerHeight < 350;
      const isSmallPhone = window.innerHeight < 400;

      let baseSize, baseGap;
      if (isVerySmallPhone) {
        // Very small phones: larger logos, tight gaps to fit
        baseSize = Math.max(70, Math.min(85, window.innerHeight * 0.26));
        baseGap = Math.max(4, Math.min(8, window.innerHeight * 0.02));
      } else if (isSmallPhone) {
        // Small phones: good sized logos
        baseSize = Math.max(75, Math.min(95, window.innerHeight * 0.26));
        baseGap = Math.max(6, Math.min(12, window.innerHeight * 0.03));
      } else {
        // Normal phones
        baseSize = Math.max(80, Math.min(120, window.innerHeight * 0.28));
        baseGap = Math.max(20, Math.min(30, window.innerHeight * 0.07));
      }

      if (count === 3) {
        // Triangle layout in left column
        return renderTriangleLayout(cryptos, baseSize, baseGap)
      } else if (count === 2) {
        // Side-by-side (vertical stack) in left column
        return renderSideBySideLayout(cryptos, baseSize, baseGap)
      } else if (count === 1) {
        // Single logo centered in left column (no enlargement in landscape)
        return renderSingleLogoLayout(cryptos[0], baseSize)
      }
    }

    return null
  }

  return (
    <>
    <Container className="main-container" style={{height: '100vh', overflow: 'hidden'}}>
  {/* Admin button (top left) - ALWAYS visible for retailers */}
  <div style={{width: '100px', textAlign:'center', height: '50px', lineHeight: '50px', left: '5px', top: '5px', color: '#efefef', borderRadius: '4px', marginTop: '10px', position: 'absolute'}} onClick={props.handleAdminClick}>
    {''}
  </div>
  {/* System button (top right) - ONLY on web for admin access */}
  {isWeb && (
    <div style={{width: '100px', textAlign:'center', height: '50px', lineHeight: '50px', right: '5px', top: '5px', color: '#efefef', borderRadius: '4px', marginTop: '10px', position: 'absolute'}} onClick={props.handleSystemClick}>
      {''}
    </div>
  )}
  <div style={{width: '100px', textAlign:'center', height: '50px', lineHeight: '50px', left: '50%', transform: 'translateX(-50%)', top: '5px', color: '#efefef', borderRadius: '4px', marginTop: '10px', position: 'absolute'}} onClick={() => window.location = '/request'}>
    {''}
  </div>

      {/* iPhone landscape ONLY: 2-column layout */}
      <div className="landscape-two-column">
        {/* Admin button (top left) - ALWAYS visible for retailers */}
        <div style={{width: '100px', textAlign:'center', height: '50px', lineHeight: '50px', left: '5px', top: '5px', color: '#efefef', borderRadius: '4px', marginTop: '10px', position: 'absolute', zIndex: 9999}} onClick={props.handleAdminClick}>
          {''}
        </div>
        {/* System button (top right) - ONLY on web for admin access */}
        {isWeb && (
          <div style={{width: '100px', textAlign:'center', height: '50px', lineHeight: '50px', right: '5px', top: '5px', color: '#efefef', borderRadius: '4px', marginTop: '10px', position: 'absolute', zIndex: 9999}} onClick={props.handleSystemClick}>
            {''}
          </div>
        )}
        <div style={{width: '100px', textAlign:'center', height: '50px', lineHeight: '50px', left: '50%', transform: 'translateX(-50%)', top: '5px', color: '#efefef', borderRadius: '4px', marginTop: '10px', position: 'absolute', zIndex: 9999}} onClick={() => window.location = '/request'}>
          {''}
        </div>

        {/* LEFT COLUMN: Dynamic crypto logos (40% width, full height) */}
        <div className="landscape-left">
          {!isLoading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              {renderLandscapeLogos(configuredCryptos, false)}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Button + Footer (60% width) */}
        <div className="landscape-right">
          {/* Upper section: Centered button and link */}
          <div className="landscape-button-section">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%'
            }}>
              {/* Custom Welcome Text - Landscape mode */}
              {props.splashImage === 'custom' && (props.topText || props.middleText || props.bottomText) && (
                <div style={{
                  textAlign: 'center',
                  width: '100%',
                  marginBottom: 'clamp(10px, 3vh, 15px)'
                }}>
                  {props.topText && (
                    <div style={{
                      fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                      fontWeight: '700',
                      color: '#374151',
                      textShadow: `${Math.max(2, customFontSize * 0.04)}px ${Math.max(2, customFontSize * 0.04)}px ${Math.max(4, customFontSize * 0.08)}px rgba(0, 0, 0, 0.15)`,
                      fontSize: `${customFontSize}px`,
                      marginBottom: `${Math.max(4, customFontSize * 0.15)}px`,
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap'
                    }}>
                      {props.topText}
                    </div>
                  )}
                  {props.middleText && (
                    <div style={{
                      fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                      fontWeight: '700',
                      color: '#374151',
                      textShadow: `${Math.max(2, customFontSize * 0.04)}px ${Math.max(2, customFontSize * 0.04)}px ${Math.max(4, customFontSize * 0.08)}px rgba(0, 0, 0, 0.15)`,
                      fontSize: `${customFontSize}px`,
                      marginBottom: `${Math.max(4, customFontSize * 0.15)}px`,
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap'
                    }}>
                      {props.middleText}
                    </div>
                  )}
                  {props.bottomText && (
                    <div style={{
                      fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                      fontWeight: '700',
                      color: '#374151',
                      textShadow: `${Math.max(2, customFontSize * 0.04)}px ${Math.max(2, customFontSize * 0.04)}px ${Math.max(4, customFontSize * 0.08)}px rgba(0, 0, 0, 0.15)`,
                      fontSize: `${customFontSize}px`,
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap'
                    }}>
                      {props.bottomText}
                    </div>
                  )}
                </div>
              )}

              {!props.storeId && (
                <>
                  <button
                    className="landscape-waitlist-button"
                    onClick={() => window.location = '/request'}
                    style={{
                      backgroundColor: 'white',
                      color: '#6B7280',
                      border: '2px solid #1E3A8A',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      padding: 'clamp(10px, 2.5vh, 12px) clamp(18px, 4vw, 24px)',
                      fontSize: 'clamp(13px, 3.5vw, 16px)',
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontWeight: '500',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.3px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#F9FAFB';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.25)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.15)';
                    }}
                  >
                    Join the <span style={{fontWeight: '700', color: '#1E3A8A'}}>Ðijitto</span><span style={{fontWeight: '700', color: '#374151'}}>Express</span> waitlist
                  </button>

                  {/* Returning user link - landscape version */}
                  <div style={{ marginTop: 'clamp(8px, 2vh, 12px)', textAlign: 'center' }}>
                    <a
                      href="/request?returning=true"
                      style={{
                        color: '#6B7280',
                        fontSize: 'clamp(9px, 2.5vw, 11px)',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        transition: 'color 0.2s ease',
                        display: 'block'
                      }}
                      onMouseOver={(e) => e.target.style.color = '#1E3A8A'}
                      onMouseOut={(e) => e.target.style.color = '#6B7280'}
                    >
                      <div>Returning user?</div>
                      <div style={{ marginTop: 'clamp(2px, 0.5vh, 3px)' }}>Enter your activation link</div>
                    </a>
                  </div>
                </>
              )}

              {/* Tip Your Server Button - Landscape - Only shown in Tip Only Mode */}
              {props.tipOnlyMode && props.storeId && (
                <div
                  onClick={() => props.handleClickSplash && props.handleClickSplash()}
                  style={{
                    marginTop: 'clamp(15px, 3vh, 25px)',
                    cursor: 'pointer',
                    padding: 'clamp(12px, 2.5vh, 18px) clamp(30px, 6vw, 50px)',
                    backgroundColor: 'white',
                    border: '2px solid #1E3A8A',
                    borderRadius: 'clamp(8px, 1.5vh, 12px)',
                    boxShadow: '0 4px 15px rgba(30, 58, 138, 0.15)',
                    transition: 'all 0.2s ease',
                    display: 'inline-block'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(30, 58, 138, 0.25)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(30, 58, 138, 0.15)';
                  }}
                >
                  <span style={{
                    fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                    fontWeight: '700',
                    fontSize: 'clamp(18px, 4vw, 28px)',
                    color: '#374151',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.15)',
                    whiteSpace: 'nowrap'
                  }}>
                    Tip Your Server
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Lower section: Footer (carousel + powered by, compact) */}
          <div className="landscape-footer-section">
            <div style={{marginBottom: 'clamp(6px, 1.5vh, 8px)', display: 'flex', justifyContent: 'center', width: '100%'}}>
              <WalletCarousel />
            </div>
            <div
              onClick={() => props.storeId && props.handleClickSplash && props.handleClickSplash()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(4px, 1vw, 6px)',
                cursor: props.storeId ? 'pointer' : 'default',
                opacity: 1,
                transition: 'opacity 0.2s ease',
                width: '100%',
                padding: '15px 30px',
                margin: '-15px -30px'
              }}
              onMouseOver={(e) => props.storeId && (e.currentTarget.style.opacity = '0.7')}
              onMouseOut={(e) => props.storeId && (e.currentTarget.style.opacity = '1')}
            >
              <span style={{fontSize: 'clamp(8px, 2.2vw, 10px)', color: '#6B7280', fontWeight: '500'}}>Powered by</span>
              <span style={{fontSize: 'clamp(8px, 2.2vw, 10px)', fontWeight: '700'}}>
                <span style={{color: '#1E3A8A'}}>Ðijitto</span><span style={{color: '#374151'}}>Express</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* DEFAULT LAYOUT (portrait + iPad landscape) */}
      <div className="default-layout">
        <center className="crypto-container" style={{position: 'fixed', top: 0, left: '50%', transform: 'translate(-50%,0)', marginTop: '5vh'}}>
          {!isLoading && (
            <>
              {/* Portrait: Dynamic rendering based on crypto count */}
              <div className={`crypto-logos-portrait count-${configuredCryptos.length}`}>
                {renderPortraitLogos(configuredCryptos, false)}
              </div>

              {/* Landscape: Dynamic rendering for iPad landscape */}
              <div className={`crypto-logos-landscape count-${configuredCryptos.length}`}>
                {renderLandscapeLogos(configuredCryptos, true)}
              </div>
            </>
          )}
        </center>

        {/* Custom Welcome Text - Only shown when custom splash is enabled */}
        {props.splashImage === 'custom' && (props.topText || props.middleText || props.bottomText) && (
          <div
            ref={textContainerRef}
            className="custom-welcome-text"
            style={{
              position: 'fixed',
              top: window.innerHeight < 700 ? '42%' : '55%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              width: '85vw',
              maxWidth: '95vw'
            }}
          >
            {props.topText && (
              <div style={{
                fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                fontWeight: '700',
                color: '#374151',
                textShadow: `${Math.max(2, customFontSize * 0.04)}px ${Math.max(2, customFontSize * 0.04)}px ${Math.max(4, customFontSize * 0.08)}px rgba(0, 0, 0, 0.15)`,
                marginBottom: `${Math.max(6, customFontSize * 0.2)}px`,
                fontSize: `${customFontSize}px`,
                lineHeight: 1.3,
                whiteSpace: 'nowrap'
              }}>
                {props.topText}
              </div>
            )}
            {props.middleText && (
              <div style={{
                fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                fontWeight: '700',
                color: '#374151',
                textShadow: `${Math.max(2, customFontSize * 0.04)}px ${Math.max(2, customFontSize * 0.04)}px ${Math.max(4, customFontSize * 0.08)}px rgba(0, 0, 0, 0.15)`,
                marginBottom: `${Math.max(6, customFontSize * 0.2)}px`,
                fontSize: `${customFontSize}px`,
                lineHeight: 1.3,
                whiteSpace: 'nowrap'
              }}>
                {props.middleText}
              </div>
            )}
            {props.bottomText && (
              <div style={{
                fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
                fontWeight: '700',
                color: '#374151',
                textShadow: `${Math.max(2, customFontSize * 0.04)}px ${Math.max(2, customFontSize * 0.04)}px ${Math.max(4, customFontSize * 0.08)}px rgba(0, 0, 0, 0.15)`,
                fontSize: `${customFontSize}px`,
                lineHeight: 1.3,
                whiteSpace: 'nowrap'
              }}>
                {props.bottomText}
              </div>
            )}
          </div>
        )}

        {
          !props.storeId ? (
            <center className="waitlist-container" style={{position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}>
              <button
                className="waitlist-button"
                onClick={() => window.location = '/request'}
                style={{
                  backgroundColor: 'white',
                  color: '#6B7280',
                  border: '2px solid #1E3A8A',
                  borderRadius: '12px',
                  padding: '18px 36px',
                  fontSize: '20px',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
                  transition: 'all 0.2s ease',
                  letterSpacing: '0.3px',
                  maxWidth: '85vw'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#F9FAFB';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.25)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.15)';
                }}
              >
                Join the <span style={{fontWeight: '700', color: '#1E3A8A'}}>Ðijitto</span><span style={{fontWeight: '700', color: '#374151'}}>Express</span> waitlist
              </button>

              {/* Returning user link - subtle text link below button */}
              <div className="returning-user-link" style={{ marginTop: '20px', textAlign: 'center' }}>
                <a
                  href="/request?returning=true"
                  style={{
                    color: '#6B7280',
                    fontSize: '14px',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    display: 'block'
                  }}
                  onMouseOver={(e) => e.target.style.color = '#1E3A8A'}
                  onMouseOut={(e) => e.target.style.color = '#6B7280'}
                >
                  <div>Returning user?</div>
                  <div style={{ marginTop: '4px' }}>Enter your activation link</div>
                </a>
              </div>
            </center>
          ) : (
            <></>
          )
        }

        {/* Tip Your Server Button - Only shown in Tip Only Mode */}
        {props.tipOnlyMode && (
          <div
            onClick={() => props.storeId && props.handleClickSplash && props.handleClickSplash()}
            style={{
              position: 'fixed',
              top: '58%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: props.storeId ? 'pointer' : 'default',
              padding: '18px 45px',
              backgroundColor: 'white',
              border: '2px solid #1E3A8A',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(30, 58, 138, 0.15)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              if (props.storeId) {
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(30, 58, 138, 0.25)';
              }
            }}
            onMouseOut={(e) => {
              if (props.storeId) {
                e.currentTarget.style.transform = 'translate(-50%, -50%)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(30, 58, 138, 0.15)';
              }
            }}
          >
            <span style={{
              fontFamily: "'Nunito', Arial, Helvetica, sans-serif",
              fontWeight: '700',
              fontSize: 'clamp(24px, 5vw, 36px)',
              color: '#374151',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.15)',
              whiteSpace: 'nowrap'
            }}>
              Tip Your Server
            </span>
          </div>
        )}

        <div className="footer-container" style={{position: 'fixed', bottom: 'max(3vh, calc(env(safe-area-inset-bottom, 0px) + 15px))', left: '50%', transform: 'translate(-50%,0)', width: '95vw', maxWidth: '600px', textAlign: 'center'}}>
          <div className="footer-content">
            <div className="wallet-section">
              <WalletCarousel />
            </div>
            <div className="powered-by-section">
              <div
                onClick={() => props.storeId && props.handleClickSplash && props.handleClickSplash()}
                style={{
                  marginTop: '12px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  cursor: props.storeId ? 'pointer' : 'default',
                  opacity: 1,
                  transition: 'opacity 0.2s ease',
                  padding: '20px 40px'
                }}
                onMouseOver={(e) => props.storeId && (e.currentTarget.style.opacity = '0.7')}
                onMouseOut={(e) => props.storeId && (e.currentTarget.style.opacity = '1')}
              >
                <span style={{
                  fontSize: window.innerWidth >= 768 && window.innerWidth <= 1024 ? '18px' : '14px',
                  color: '#6B7280',
                  fontWeight: '500',
                  lineHeight: window.innerWidth >= 768 && window.innerWidth <= 1024 ? '35px' : '28px'
                }}>Powered by</span>
                <span style={{
                  fontSize: window.innerWidth >= 768 && window.innerWidth <= 1024 ? '18px' : '14px',
                  fontWeight: '700',
                  lineHeight: window.innerWidth >= 768 && window.innerWidth <= 1024 ? '35px' : '28px'
                }}>
                  <span style={{color: '#1E3A8A'}}>Ðijitto</span><span style={{color: '#374151'}}>Express</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ALL CSS */}
      <style>{`
        /* Hide iPhone landscape 2-column layout by default */
        .landscape-two-column {
          display: none;
        }

        /* Show default layout by default */
        .default-layout {
          display: block;
        }

        /* iPhone landscape: Show 2-column, hide default */
        @media (max-height: 450px) and (orientation: landscape) {
          .landscape-two-column {
            display: flex !important;
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            padding: clamp(5px, 2vh, 10px);
          }

          .landscape-left {
            width: 40%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .landscape-right {
            width: 60%;
            height: 100%;
            display: flex;
            flex-direction: column;
            padding-left: clamp(10px, 3vw, 15px);
          }

          .landscape-button-section {
            flex: 1.2;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .landscape-footer-section {
            flex: 0.8;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-end;
            text-align: center;
            padding-bottom: max(clamp(10px, 3vh, 15px), calc(env(safe-area-inset-bottom, 0px) + 10px));
          }

          .landscape-footer-section > * {
            margin-left: auto;
            margin-right: auto;
          }

          /* Hide default layout on iPhone landscape */
          .default-layout {
            display: none !important;
          }
        }

        /* Very small phone landscape - further reduced sizing */
        @media (max-height: 350px) and (orientation: landscape) {
          .landscape-two-column {
            padding: clamp(2px, 1vh, 5px);
          }

          .landscape-left {
            width: 35%;
          }

          .landscape-left img {
            max-height: 70px !important;
            max-width: 70px !important;
            height: 70px !important;
            width: 70px !important;
          }

          .landscape-right {
            width: 65%;
            padding-left: clamp(5px, 2vw, 10px);
          }

          .landscape-button-section {
            flex: 1;
          }

          .landscape-footer-section {
            flex: 1;
            padding-bottom: max(clamp(5px, 2vh, 10px), calc(env(safe-area-inset-bottom, 0px) + 5px));
          }

          .landscape-waitlist-button {
            padding: clamp(6px, 1.5vh, 8px) clamp(12px, 3vw, 16px) !important;
            font-size: clamp(11px, 3vw, 13px) !important;
          }
        }

        /* Small phone landscape (350-400px height) */
        @media (min-height: 350px) and (max-height: 400px) and (orientation: landscape) {
          .landscape-left {
            width: 38%;
          }

          .landscape-left img {
            max-height: 85px !important;
            max-width: 85px !important;
            height: 85px !important;
            width: 85px !important;
          }

          .landscape-right {
            width: 62%;
          }

          .landscape-waitlist-button {
            padding: clamp(8px, 2vh, 10px) clamp(14px, 3.5vw, 18px) !important;
            font-size: clamp(12px, 3.2vw, 14px) !important;
          }
        }

        /* Normal phone landscape (400-450px height) */
        @media (min-height: 400px) and (max-height: 450px) and (orientation: landscape) {
          .landscape-left img {
            max-height: 95px !important;
            max-width: 95px !important;
            height: 95px !important;
            width: 95px !important;
          }
        }

        /* Portrait - show triangle layout */
        @media (orientation: portrait) {
          .crypto-logos-portrait {
            display: flex !important;
          }
          .crypto-logos-landscape {
            display: none !important;
          }
          .waitlist-container {
            top: 52% !important;
          }
        }

        /* iPad landscape - show horizontal layout */
        @media (min-width: 768px) and (orientation: landscape) {
          .crypto-logos-portrait {
            display: none !important;
          }
          .crypto-logos-landscape {
            display: flex !important;
          }
          .crypto-logos-landscape img {
            height: 210px !important;
            width: 210px !important;
          }
          .waitlist-container {
            top: 50% !important;
          }
        }

        /* iPad portrait - larger logos (default for 2-3 cryptos) */
        @media (min-width: 768px) and (orientation: portrait) {
          .crypto-logos-portrait img {
            height: 210px !important;
            width: 210px !important;
          }
          /* Single logo: 50% larger */
          .crypto-logos-portrait.count-1 img {
            height: 315px !important;
            width: 315px !important;
          }
        }

        /* iPhone portrait - medium logos (default for 2-3 cryptos) */
        @media (max-width: 767px) and (orientation: portrait) {
          .crypto-logos-portrait img {
            height: 120px !important;
            width: 120px !important;
          }
          /* Single logo: 50% larger */
          .crypto-logos-portrait.count-1 img {
            height: 180px !important;
            width: 180px !important;
          }
        }

        /* Small phone portrait - balanced sizing */
        @media (max-width: 767px) and (max-height: 700px) and (orientation: portrait) {
          .crypto-logos-portrait img {
            height: 100px !important;
            width: 100px !important;
          }
          .crypto-logos-portrait.count-1 img {
            height: 150px !important;
            width: 150px !important;
          }
          .waitlist-container {
            top: 50% !important;
          }
          .crypto-container {
            margin-top: 2vh !important;
          }
          .waitlist-button {
            padding: 14px 28px !important;
            font-size: 17px !important;
          }
        }

        /* Very small phone portrait - compact but readable */
        @media (max-width: 767px) and (max-height: 600px) and (orientation: portrait) {
          .crypto-logos-portrait img {
            height: 85px !important;
            width: 85px !important;
          }
          .crypto-logos-portrait.count-1 img {
            height: 120px !important;
            width: 120px !important;
          }
          .waitlist-container {
            top: 48% !important;
          }
          .crypto-container {
            margin-top: 1vh !important;
          }
          .waitlist-button {
            padding: 10px 20px !important;
            font-size: 14px !important;
            max-width: 80vw !important;
          }
          .returning-user-link {
            margin-top: 8px !important;
          }
          .returning-user-link a {
            font-size: 11px !important;
          }
          .returning-user-link div {
            margin-top: 2px !important;
          }
          .footer-container {
            bottom: max(1vh, calc(env(safe-area-inset-bottom, 0px) + 10px)) !important;
          }
          .wallet-section {
            transform: scale(0.85);
          }
        }

        /* Equalize Doge logo size (all orientations) */
        .crypto-logos-portrait img[alt="Dogecoin"],
        .crypto-logos-landscape img[alt="Dogecoin"],
        .landscape-doge-logo,
        .doge-logo {
          transform: scale(1.08) !important;
        }

        /* Custom welcome text positioning - dynamic font sizes handled by JS */
        /* Tablet portrait - center text between logos and footer */
        @media (min-width: 768px) and (orientation: portrait) {
          .custom-welcome-text {
            top: 52% !important;
          }
        }

        /* Medium tablet portrait - adjust positioning */
        @media (min-width: 768px) and (max-width: 900px) and (orientation: portrait) {
          .custom-welcome-text {
            top: 55% !important;
          }
        }

        /* Large tablet portrait (Pixel Tablet size) */
        @media (min-width: 901px) and (orientation: portrait) {
          .custom-welcome-text {
            top: 52% !important;
          }
        }

        /* iPhone landscape - 28px (handled in inline styles) */

        /* Footer default: stacked layout */
        .footer-content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .wallet-section {
          width: 100%;
        }

        .powered-by-section {
          width: 100%;
        }
      `}</style>

    </Container>
    </>
  )
}

export default Splash
